import {
  FetchEventSourceInit,
  fetchEventSource,
  EventSourceMessage,
} from "@microsoft/fetch-event-source";
import { isAgentEvent } from "@/utils/agentEvent";

export interface CustomConfig extends FetchEventSourceInit {
  onerror?: (err: any, errInfo?: any) => number | void | null | undefined;
  onRetry?: () => void;
  onclose?: (type?: string) => void;
  onmessage: (e: EventSourceMessage, data?: any) => void;
  timeout?: number | (() => number);
  tryTimes?: number;
  /** 仅聊天诊断启用来源标记，其他 SSE 功能保持原有错误对象约定。 */
  includeErrorOrigin?: boolean;
  /** 要求服务端显式发送 [DONE]，仅用于严格终态的文件对话。 */
  requireDone?: boolean | (() => boolean);
  /** agent-v1 必须以唯一终态或 deferred 结束当前连接。 */
  requireAgentTerminal?: boolean | (() => boolean);
}

// sse结束标识
const EXP_END_SIGN = /\uD83D\uDC4D/;

export const eventSource = (input: RequestInfo, config: CustomConfig): void => {
  const {
    signal,
    onRetry,
    timeout = 10000,
    tryTimes = 3,
    includeErrorOrigin = false,
    requireDone = false,
    requireAgentTerminal = false,
    onmessage: _onmessage,
    onclose: _onclose,
    onerror: _onerror,
    ...sourceConfig
  } = config;

  const resolveTimeout = () =>
    typeof timeout === "function" ? timeout() : timeout;
  console.log("超时timeout", resolveTimeout());

  let startDate = Date.now();
  let timerId: number | null;
  let requestTimes = 0;
  // let msgStr = ''
  let abortController: AbortController | null = new AbortController();
  let isEnded = false;
  let receivedDone = false;
  let receivedAgentEnd = false;
  let userAborted = false;
  let closeNotified = false;
  const shouldRequireDone = () =>
    typeof requireDone === "function" ? requireDone() : requireDone;
  const shouldRequireAgentTerminal = () =>
    typeof requireAgentTerminal === "function"
      ? requireAgentTerminal()
      : requireAgentTerminal;

  // 取消请求
  const cancel = () => {
    timerId && clearInterval(timerId);
    timerId = null;
    abortController?.abort();
    abortController = null;
  };

  signal?.addEventListener("abort", () => {
    userAborted = true;
    cancel();
    onclose("abort");
  });

  // 超时重连检测
  const startCheckTimeout = () => {
    timerId && clearInterval(timerId);
    timerId = setTimeout(() => {
      if (Date.now() - startDate > resolveTimeout()) {
        cancel();
        onerror("请求超时", includeErrorOrigin ? { code: "CLIENT_TIMEOUT" } : undefined);
      } else {
        startCheckTimeout();
      }
    }, 200) as unknown as number;
  };

  // 重连
  const retry = () => {
    requestTimes += 1;
    abortController && abortController.abort();
    abortController = new AbortController();
    startDate = Date.now();
    onRetry?.();
    request();
  };

  // 接受消息
  const onmessage = (ev: EventSourceMessage) => {
    // 异常
    if (/lbError:/.test(ev.data)) {
      try {
        const data = JSON.parse(ev.data.replace("lbError:", ""));
        onerror(data.msg, data, true, "server");
        return;
      } catch (err) {
        onerror(includeErrorOrigin ? "服务端错误消息格式无效" : "", {}, true, "protocol");
        return;
      }
    }
    // console.log(ev);

    // msgStr += ev.data
    let str = ev.data.replace(EXP_END_SIGN, "");

    str = str.replace(/<laiBo\/>/g, "\n ");
    str = str.replace(/<laiBoSpace\/>/g, " ");
    startDate = Date.now();
    startCheckTimeout();

    if (str === "[DONE]") {
      receivedDone = true;
      cancel();
      onclose("done");
      return;
    }

    let messageId = "";
    let params;
    let newEv = { ...ev, data: str, id: ev.id || messageId };
    try {
      params = JSON.parse(str);

      // agent-v1 必须把完整 envelope 交给业务 reducer，不能走 choices 文本转换。
      const agentEvent = isAgentEvent(params) ? params : undefined;
      if (ev.event === "agent" && !agentEvent) {
        onerror(
          "Agent 事件格式无效",
          { code: "AGENT_EVENT_INVALID" },
          true,
          "protocol",
        );
        return;
      }
      if (ev.event === "agent" || agentEvent) {
        newEv = {
          ...ev,
          event: "agent",
          data: str,
          id:
            ev.id ||
            (agentEvent
              ? `${agentEvent.requestId}:${agentEvent.sequence}`
              : messageId),
        };
        _onmessage?.(newEv, params);
        if (
          agentEvent &&
          [
            "response.completed",
            "response.failed",
            "response.deferred",
          ].includes(agentEvent.type)
        ) {
          receivedAgentEnd = true;
          cancel();
          onclose(agentEvent.type);
        }
        return;
      }

      if (params.choices) {
        str = params.choices
          .map((item: any) => item.delta?.reasoning_content || item.delta?.content || "")
          .join("");
        messageId = params.id;
      } else if (params.code === -1 || params.code === -10020) {
        // The server completed the SSE response with a deterministic business
        // rejection. Reposting the side-effecting /stream request cannot recover
        // it and only creates a retry storm with the same requestId.
        onerror(params.msg, params, true, "server");
        return;
      }

      let event = ev.event;
      if (
        params.choices &&
        params.choices[0] &&
        params.choices[0].finish_reason == "length"
      ) {
        event = "continue";
      } else if (
        params.choices &&
        params.choices[0] &&
        params.choices[0].finish_reason == "stop"
      ) {
        event = "stop";
      }
      newEv = { ...ev, event, data: str, id: ev.id || messageId };
    } catch (err) {}
    if (isEnded) return;
    _onmessage?.(newEv, params);
    if (newEv.event === "stop" && !shouldRequireDone()) {
      cancel();
      onclose("stop");
    }
  };

  // 会话结束
  const onclose = (type?: string) => {
    timerId && clearInterval(timerId);
    timerId = null;
    abortController = null;
    if (shouldRequireDone() && !receivedDone && !userAborted && !isEnded) {
      onerror(
        "响应未完整结束，请重新生成",
        { code: "STREAM_INCOMPLETE", partial: true },
        true,
      );
      return;
    }
    if (
      shouldRequireAgentTerminal() &&
      !receivedAgentEnd &&
      !userAborted &&
      !isEnded
    ) {
      onerror(
        "Agent 响应未完整结束，正在恢复",
        { code: "AGENT_STREAM_INCOMPLETE", partial: true },
      );
      return;
    }
    // 走msg消息结束的请求可能存在error之后触发close
    if (!isEnded && !closeNotified) {
      closeNotified = true;
      _onclose?.(type);
    }
  };

  // 请求异常
  const onerror = (errMsg: any, errInfo?: any, stopRequest?: boolean,
    errorOrigin: "server" | "transport" | "protocol" = "transport") => {
    if (isEnded || userAborted || receivedDone || receivedAgentEnd) return;
    timerId && clearInterval(timerId);
    timerId = null;
    abortController = null;

    const originalErrInfo =
      errInfo || (typeof errMsg === "object" && errMsg ? errMsg : undefined);
    const normalizedErrInfo = includeErrorOrigin
      ? { ...originalErrInfo, errorOrigin }
      : originalErrInfo;
    const errorCode = String(normalizedErrInfo?.code || "");
    const errorStatus = Number(normalizedErrInfo?.status);
    const mustStopRetry =
      stopRequest ||
      [
        "AGENT_STREAM_RECOVERY_REQUIRED",
        "AGENT_STREAM_INCOMPLETE",
        "AGENT_REQUEST_CONFLICT",
        "AGENT_PROTOCOL_CONFLICT",
      ].includes(errorCode) ||
      (errorStatus >= 400 && errorStatus < 500);

    if (!mustStopRetry && tryTimes && requestTimes < tryTimes) {
      retry();
    } else {
      isEnded = true;
      const defaultErrorMsg = "哎呀，一下卡住了，不知道说什么了^_^";
      //  errMsg 不存在 或则是 fetchEventSource抛出的内部英文错误 使用默认错误提示 否则使用解析出来的错误提示
      const rawErrorMessage =
        errMsg instanceof Error ? errMsg.message : String(errMsg || "");
      let errorMsg =
        rawErrorMessage.startsWith("Expected content-type to be") ||
        !rawErrorMessage
          ? defaultErrorMsg
          : rawErrorMessage;
      _onerror?.(errorMsg, normalizedErrInfo);
      cancel();
      onclose();
      throw Error("");
    }
  };

  // 发起请求
  const request = () => {
    fetchEventSource(input, {
      ...sourceConfig,
      onmessage,
      onclose,
      onerror,
      signal: abortController?.signal,
      openWhenHidden: true,
    });
    startCheckTimeout();
  };

  request();
};
